import ArrowBack from "@mui/icons-material/ArrowBack";
import Alert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AssignOrganizationModal from "@/components/admin/collections/AssignOrganizationModal";
import CollectionProductCard from "@/components/admin/collections/CollectionProductCard";
import { GfgButton } from "@/components/ui/GfgButton";
import {
  COLLECTIONS_BACK_LABEL,
  EMPTY_TEXT,
  LOAD_FALLBACK,
  LOADING_LABEL,
  RETRY_LABEL,
} from "@/constants/adminCollections";
import { useAdminCollectionProducts } from "@/hooks/useAdminCollections";
import { useOrganizationFilterOptions } from "@/hooks/useOrganizations";
import type { AdminCollectionProduct } from "@/interfaces/adminCollections";
import { buildOrganizationOptions, formatProductCount } from "@/utils/adminCollections-helper";
import { apiErrorMessage } from "@/utils/apiError-helper";

const LIST_PATH = "/admin/collections";
const TOAST_MS = 4000;

interface ToastState {
  message: string;
  severity: "success" | "error";
}

const GRID_CSS = `
.col-detail-grid{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:16px}
@media (max-width:1100px){.col-detail-grid{grid-template-columns:repeat(3,minmax(0,1fr))}}
@media (max-width:760px){.col-detail-grid{grid-template-columns:repeat(2,minmax(0,1fr))}}
`;

export default function AdminCollectionDetail() {
  const { collectionId } = useParams<{ collectionId: string }>();
  const navigate = useNavigate();
  const { data, isLoading, isError, error, refetch } = useAdminCollectionProducts(collectionId);
  const { data: orgFilterOptions } = useOrganizationFilterOptions();
  const [assigning, setAssigning] = useState<AdminCollectionProduct | null>(null);
  const [toast, setToast] = useState<ToastState | null>(null);
  const [toastOpen, setToastOpen] = useState(false);

  const organizationOptions = useMemo(
    () => buildOrganizationOptions(orgFilterOptions ?? []),
    [orgFilterOptions],
  );

  function showToast(message: string, severity: ToastState["severity"]) {
    setToast({ message, severity });
    setToastOpen(true);
  }

  // Re-read the product from the refetched list so the modal never prefills a stale assignment.
  const activeProduct = assigning
    ? (data?.products.find((p) => p.id === assigning.id) ?? assigning)
    : null;

  return (
    <div>
      <style>{GRID_CSS}</style>
      <button
        type="button"
        onClick={() => navigate(LIST_PATH)}
        className="mb-3 inline-flex cursor-pointer items-center gap-1 border-none bg-transparent p-0 font-lato text-graphite text-sm"
      >
        <ArrowBack sx={{ fontSize: 18 }} /> {COLLECTIONS_BACK_LABEL}
      </button>

      {isLoading && <p className="font-lato text-graphite text-sm">{LOADING_LABEL}</p>}

      {isError && (
        <div role="alert" className="flex items-center gap-3 font-lato text-good-wine text-sm">
          <span>{apiErrorMessage(error, LOAD_FALLBACK)}</span>
          <GfgButton type="button" variant="outline" onClick={() => refetch()}>
            {RETRY_LABEL}
          </GfgButton>
        </div>
      )}

      {data && (
        <>
          <div className="mb-5">
            <h2 className="font-crimson text-[28px] text-rodeo-black">{data.collection.name}</h2>
            <p className="font-lato text-graphite text-sm">
              {formatProductCount(data.products.length)}
            </p>
          </div>

          {data.products.length === 0 ? (
            <p className="py-4 font-lato text-graphite text-sm">{EMPTY_TEXT}</p>
          ) : (
            <div className="col-detail-grid">
              {data.products.map((product) => (
                <CollectionProductCard
                  key={product.id}
                  product={product}
                  isCustom={data.collection.isCustom}
                  onAssign={setAssigning}
                />
              ))}
            </div>
          )}

          {data.collection.isCustom && activeProduct && (
            <AssignOrganizationModal
              key={activeProduct.id}
              product={activeProduct}
              organizationOptions={organizationOptions}
              priceTiers={data.priceTiers}
              onClose={() => setAssigning(null)}
              onSuccess={(message) => showToast(message, "success")}
              onError={(message) => showToast(message, "error")}
            />
          )}
        </>
      )}

      <Snackbar
        open={toastOpen}
        autoHideDuration={TOAST_MS}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={toast?.severity ?? "success"}
          variant="filled"
          onClose={() => setToastOpen(false)}
        >
          {toast?.message}
        </Alert>
      </Snackbar>
    </div>
  );
}
